import React, { useState } from 'react'
import { Group, Image, TextInput, Container, Text, Anchor, Menu, Center, Button, ActionIcon, useMantineTheme, Burger } from '@mantine/core'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useMediaQuery } from '@mantine/hooks'
import { IconUserCircle, IconSearch } from '@tabler/icons-react'
import classes from './Header.module.css'
import etfNexusLogoDark from '@/assets/img/logo_dark.png'
import { ReactComponent as IcSearch } from '@/assets/icon/ic_search.svg'
import { ReactComponent as IcSearchThick } from '@/assets/icon/ic_search_thick.svg'
import { ReactComponent as IcHamburgerMenu } from '@/assets/icon/ic_hamburger-menu.svg'
import { ReactComponent as LogoSvg } from '@/assets/svg/logo.svg'
import { useUserContext } from '@/context/UserContext'
import { AuthModal } from '@/components/auth/modals/AuthModal'
import Popups from '@/constants/popups'
import icPersonPng from '@/assets/icon/ic_person.png'
import { useAuthModal } from '@/context/AuthModalContext'
import { LAYOUT_CONSTANTS } from '@/constants/styles'

// Define navigation items
const navItems = [
  { label: 'News', link: '/news' },
  { label: 'Screener', link: '/screener' },
  { label: 'Compare', link: '/etf/comparison' },
  { label: 'Blog', link: '/blog' }
]

const Header = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useMantineTheme()
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`)
  const isTablet = useMediaQuery(`(min-width: ${theme.breakpoints.md})`)
  const { user, logout, deleteAccount } = useUserContext()
  const { opened: authModalOpened, step: authStep, openAuthModal, closeAuthModal } = useAuthModal()
  const [searchValue, setSearchValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)

  const openLoginModal = () => {
    openAuthModal('email')
  }

  const openSignupModal = () => {
    openAuthModal('email')
  }

  const handleSearch = () => {
    const trimmed = searchValue.trim()
    if (!trimmed) return
    const currentParams = new URLSearchParams(location.search)
    const currentSearch = currentParams.get('search') || ''
    let url = `/screener?search=${encodeURIComponent(trimmed)}`
    // If already on screener and search is the same, force reload by adding a dummy param
    if (location.pathname === '/screener' && currentSearch === trimmed) {
      url += `&_force=${Date.now()}`
    }
    navigate(url)
    setSearchValue('')
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchValue.trim()) {
      handleSearch()
    }
  }

  // Generate navigation items
  const generateNavItems = () => {
    return navItems.map((item) => {
      const isActive = location.pathname.startsWith(item.link) || (location.pathname === '/' && item.link === '/news')
      return (
        <Anchor
          key={item.label}
          href={item.link}
          className={classes.navItem}
          mx={'sm'}
          size="sm"
          fw={500}
          c={isActive ? 'blue.5' : 'grey.7'}
          td="none"
          data-active={isActive}>
          {item.label}
        </Anchor>
      )
    })
  }

  const handleLogout = () => {
    Popups.confirmLogout(async () => {
      await logout()
      navigate('/')
    })
  }

  const handleDelete = () => {
    Popups.confirmDelete(async () => {
      await deleteAccount()
    })
  }

  return (
    <header className={classes.header}>
      <Container fluid py={{ base: 'md', md: 'lg' }}>
        <Group justify="space-between" w={{ base: '100%', xl: `${LAYOUT_CONSTANTS.CONTENT_AREA_WIDTH}px` }} px="lg" align="center" style={{ margin: '0 auto' }}>
          <div className={classes.logoWrapper}>
            <Image src={etfNexusLogoDark} alt="ETF NEXUS" style={{ cursor: 'pointer' }} onClick={() => navigate('/')} />
          </div>

          {/* Desktop - Full layout */}
          {isDesktop && (
            <Group>
              {generateNavItems()}
              <Group>
                <TextInput
                  style={{ height: 'auto!important' }}
                  placeholder="Search for ETFs"
                  className={[classes.searchInput, isFocused || searchValue ? classes.searchInputActive : ''].join(' ')}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  rightSection={
                    <ActionIcon
                      variant="transparent"
                      aria-label="Search"
                      onClick={handleSearch}
                      disabled={!searchValue.trim()}
                      bg="transparent"
                      style={{ cursor: searchValue.trim() ? 'pointer' : 'not-allowed' }}
                      tabIndex={searchValue.trim() ? 0 : -1}>
                      <IcSearch style={{ color: 'var(--mantine-color-grey-8)' }} />
                    </ActionIcon>
                  }
                />
                <Menu shadow="md" width={140} position="bottom-end">
                  <Menu.Target>
                    <Anchor className={classes.profileIcon}>
                      {user ? (
                        <Image src={icPersonPng} width={28} height={28} alt="User" />
                      ) : (
                        <Center bg="gray.3" h={28} w={28} style={{ borderRadius: '24px' }}>
                          <IconUserCircle size={18} color={'black'} />
                        </Center>
                      )}
                    </Anchor>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {user ? (
                      <>
                        {user.qtyEtfsOnWatchList > 0 && <Menu.Item onClick={() => navigate('/screener?watchlistOnly=true')}>My Watchlist</Menu.Item>}
                        <Menu.Item onClick={handleLogout}>Log out</Menu.Item>
                        <Menu.Item onClick={handleDelete}>Delete account</Menu.Item>
                      </>
                    ) : (
                      <>
                        <Menu.Item onClick={openLoginModal}>Log in</Menu.Item>
                        <Menu.Item onClick={openSignupModal}>Sign up</Menu.Item>
                      </>
                    )}
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>
          )}

          {/* Tablet - Nav links + search icon + user menu */}
          {!isDesktop && isTablet && (
            <Group>
              {generateNavItems()}
              <Group>
                <ActionIcon variant="transparent" aria-label="Search" onClick={() => navigate('/screener?searchFocus=true')} bg="transparent">
                  <IcSearchThick style={{ color: 'var(--mantine-color-grey-8)' }} />
                </ActionIcon>
                <Menu shadow="md" width={140} position="bottom-end">
                  <Menu.Target>
                    <Anchor className={classes.profileIcon}>
                      {user ? (
                        <Image src={icPersonPng} width={28} height={28} alt="User" />
                      ) : (
                        <Center bg="gray.3" h={28} w={28} style={{ borderRadius: '24px' }}>
                          <IconUserCircle size={18} color={'black'} />
                        </Center>
                      )}
                    </Anchor>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {user ? (
                      <>
                        {user.qtyEtfsOnWatchList > 0 && <Menu.Item onClick={() => navigate('/screener?watchlistOnly=true')}>My Watchlist</Menu.Item>}
                        <Menu.Item onClick={handleLogout}>Log out</Menu.Item>
                        <Menu.Item onClick={handleDelete}>Delete account</Menu.Item>
                      </>
                    ) : (
                      <>
                        <Menu.Item onClick={openLoginModal}>Log in</Menu.Item>
                        <Menu.Item onClick={openSignupModal}>Sign up</Menu.Item>
                      </>
                    )}
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>
          )}

          {/* Mobile - Search icon + hamburger menu */}
          {!isTablet && (
            <Group>
              <ActionIcon variant="transparent" aria-label="Search" onClick={() => navigate('/screener?searchFocus=true')} bg="transparent">
                <IcSearchThick style={{ color: 'var(--mantine-color-grey-8)' }} />
              </ActionIcon>
              <Menu shadow="md" width={200} position="bottom-end">
                <Menu.Target>
                  <ActionIcon 
                  aria-label="Open navigation menu"
                  variant="transparent"
                  bg="transparent">
                    <IcHamburgerMenu style={{ color: 'var(--mantine-color-grey-8)' }} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {/* Navigation Links */}
                  {navItems.map((item) => {
                    const isActive = location.pathname.startsWith(item.link) || (location.pathname === '/' && item.link === '/news')
                    return (
                      <Menu.Item key={item.label} onClick={() => navigate(item.link)} c={isActive ? 'blue.5' : 'grey.8'}>
                        {item.label}
                      </Menu.Item>
                    )
                  })}

                  <Menu.Divider />

                  {/* User Menu Items */}
                  {user ? (
                    <>
                      {user.qtyEtfsOnWatchList > 0 && <Menu.Item onClick={() => navigate('/screener?watchlistOnly=true')}>My Watchlist</Menu.Item>}
                      <Menu.Item onClick={handleLogout}>Log out</Menu.Item>
                      <Menu.Item onClick={handleDelete}>Delete account</Menu.Item>
                    </>
                  ) : (
                    <>
                      <Menu.Item onClick={openLoginModal}>Log in</Menu.Item>
                      <Menu.Item onClick={openSignupModal}>Sign up</Menu.Item>
                    </>
                  )}
                </Menu.Dropdown>
              </Menu>
            </Group>
          )}
        </Group>
      </Container>

      <AuthModal opened={authModalOpened} onClose={closeAuthModal} initialStep={authStep} />
    </header>
  )
}

export default Header
